
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { queryCount, queryDistinctValues, queryStatistics, queryLayer } from '../../api/arcgisApi';
import { config } from '../../appConfig'
import { setPriorityOptions, setTreatmentOptions, setSelectedLink, setStructureOptions } from '../store/action/globalActions';
import {
  Typography,
  Grid,
  Paper,
  Box,
  IconButton,
} from '@mui/material';
import { styled } from "@mui/material/styles";

import DataCard, { DataCardProps } from '../components/DataCard';
import InfoBoxAlert from '../components/InfoBoxAlert';
import MapComponent from '../components/Map'
import FilterWidget from '../components/widgets/FilterWidget';

const drawerWidth = 320;

// Toggle button, always visible, fixed on right edge
const ToggleButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  borderRadius: '24px 0 0 24px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));


export default function OverviewPage() {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [rightSidebarGridSize, setRightSidebarGridSize] = useState(0);
  const handleToggle = () => setOpen((prev) => !prev);

  useEffect(() => {
    if (open) { setRightSidebarGridSize(2.5) } else { setRightSidebarGridSize(0) }
    dispatch(setSelectedLink("Gregory Highway"));
  }, [open])

  const [prioritiesTotalCount, setPrioritiesTotalCount] = useState<number>(0);
  const [capacityOvertakingPrioritiesTotalCount, setCapacityOvertakingPrioritiesTotalCount] = useState<number>(0);
  const [structuresTotalCount, setStructuresTotalCount] = useState<number>(0);
  const [intersectionTotalCount, setIntersectionTotalCount] = useState<number>(0);
  const [sectionsTotalCount, setSectionsTotalCount] = useState<number>(0);
  const [linkTotalKM, setLinkTotalKM] = useState<number>(0);

  const cards: DataCardProps[] = [
    {
      title: 'Total link length',
      value: `${linkTotalKM?.toFixed(2)} km`,
      description: 'From sections / capacity extents',
      borderColor: 'rgb(0, 119, 255)',
      icon: 'measure-line'
    },
    {
      title: 'Total Sections',
      value: sectionsTotalCount?.toString(),
      description: 'From sections / capacity extents',
      borderColor: 'rgb(128, 128, 0)',
      icon: 'map'
    },
    {
      title: 'Major Intersection Upgrades',
      value: intersectionTotalCount?.toString(),
      description: 'From sections / capacity extents',
      borderColor: 'rgb(138, 43, 226)',
      icon: 'curve'
    },
    {
      title: 'Structures',
      value: structuresTotalCount?.toString(),
      description: 'From sections / capacity extents',
      borderColor: 'rgb(255, 165, 0)',
      icon: 'urban-model'
    },
    {
      title: 'Formation Widening Priority Sites',
      value: prioritiesTotalCount?.toString(),
      description: 'From sections / capacity extents',
      borderColor: 'rgb(206, 4, 139)',
      icon: 'flag'
    },
    {
      title: 'Capacity/Overtaking Priority Sites',
      value: capacityOvertakingPrioritiesTotalCount?.toString(),
      description: 'From sections / capacity extents',
      borderColor: 'rgb(165, 165, 255)',
      icon: 'flag'
    },
  ]

  // const [priorityOptions, setPriorityOptions] = useState<{ label: string, id: number, color: string }[]>([]);
  const priorityOptions = useSelector((state: any) => state.global.priorityOptions);
  const [selectedPriority, setSelectedPriority] = useState<string[]>([]);
  const treatmentOptions = useSelector((state: any) => state.global.treatmentOptions);
  const [selectedTreatment, setSelectedTreatment] = useState<string[]>([]);
  const [view, setView] = useState<any>(null)
  const structureOptions = useSelector((state: any) => state.global.structureOptions);
  const [selectedStructure, setSelectedStructure] = useState<string[]>([]);
  const [map, setMap] = useState<any>(null)

  useEffect(() => {
    const loadPriorityValues = async () => {
      try {
        const result = await queryDistinctValues(4, 'priority');
        dispatch(
          setPriorityOptions(result.features.map((feature: any) => ({ id: feature.attributes.priority, label: feature.attributes.priority, color: feature.attributes.priority === 'P1' ? 'red' : feature.attributes.priority === 'P2' ? 'orange' : feature.attributes.priority === 'P3' ? 'yellow' : 'gray' })))
        );
        setSelectedPriority(result.features.map((feature: any) => feature.attributes.priority));
      } catch (error) {
        console.error("Failed to load features", error);
      }
    };
    const loadTreatmentValues = async () => {
      try {
        const result = await queryDistinctValues(4, 'treatment');
        dispatch(
          setTreatmentOptions(result.features.map((feature: any) => ({ id: feature.attributes.treatment, label: feature.attributes.treatment, color: '' })))
        );
        setSelectedTreatment(result.features.map((feature: any) => feature.attributes.treatment));
      } catch (error) {
        console.error("Failed to load features", error);
      }
    };
    loadPriorityValues();
    loadTreatmentValues();
    const loadPrioritiesTotalCount = async () => {
      try {
        const result = await queryCount(4);
        setPrioritiesTotalCount(result);
      } catch (error) {
        console.error("Failed to load total count", error);
      }
    };
    loadPrioritiesTotalCount();
    const loadStructuresTotalCount = async () => {
      try {
        const result = await queryCount(3);
        setStructuresTotalCount(result);
      } catch (error) {
        console.error("Failed to load total count", error);
      }
    };
    loadStructuresTotalCount();
    const loadMajorIntersectionTotalCount = async () => {
      try {
        const result = await queryLayer(3133, '1=1' ,'https://aus.gis.arcadis.com/hosting/rest/services/Hosted/INLAND_FR_Intersection_Upgrades/FeatureServer')
        // console.log(result);
        setIntersectionTotalCount(result?.features?.length || 0)
      } catch (error) {
        console.error("Failed to load major intersection total count", error);
      }
    }
    loadMajorIntersectionTotalCount()
    const loadCapacityOvertakingPriorityTotalCount = async () => {
      try {
        const result = await queryCount(7);
        setCapacityOvertakingPrioritiesTotalCount(result)
      } catch (error) {
        console.error("Failed to load capacity/overtaking priority total count", error);
      }
    }
    loadCapacityOvertakingPriorityTotalCount()
    const loadSectionsTotalCount = async () => {
      try {
        const result = await queryCount(2);
        setSectionsTotalCount(result);
      } catch (error) {
        console.error("Failed to load total count", error);
      }
    };
    loadSectionsTotalCount();

    const loadLinkTotal = async () => {
      try {
        const result = await queryStatistics(1, 'sum', 'SHAPE__Length', 'totalLength');
        setLinkTotalKM(result / 1000);
      } catch (error) {
        console.error("Failed to load total count", error);
      }
    };
    loadLinkTotal();
    const loadStructureValues = async () => {
      try {
        const result = await queryDistinctValues(3, 'asset_type');
        dispatch(
          setStructureOptions(result?.features?.map((feature: any) => ({ id: feature.attributes.asset_type, label: feature.attributes.asset_type, color: '' })))
        );
        setSelectedStructure(result?.features?.map((feature: any) => feature.attributes.asset_type));
      } catch (error) {
        console.error("Failed to load features", error);
      }
    };
    loadStructureValues();
  }, [dispatch]);

  const handleViewChange = (event: Event) => {
    
    const changeView = ((event as any).target.view);
    setView(changeView)
    const changeMap = ((event as any).target.map);
    setMap(changeMap)

    async function fetchUpdate() {
      const structures = changeView?.layerViews?.items?.find((l: any) => l.layer.title === "Structures");
      const intersection = changeView?.layerViews?.items?.find((l: any) => l.layer.title === "Major Intersection Upgrades");
      const priority = changeView?.layerViews?.items?.find((l: any) => l.layer.title === "27A Road Link Plan - Formation Widening Priorities");
      const capacityPriority = changeView?.layerViews?.items?.find((l: any) => l.layer.title === "27A Road Link Plan - Capacity / Overtaking Lane Widening Priorities");
      const structuresFeatureSet = await structures?.queryFeatures({
        geometry: changeView.extent
      });
      const priorityFeatureSet = await priority?.queryFeatures({
        geometry: changeView.extent
      });
      const capacityPriorityFeatureSet = await capacityPriority?.queryFeatures({
        geometry: changeView.extent
      });
      const intersectionFeatureSet = await intersection?.queryFeatures({
        geometry: changeView.extent
      });
      if (!isNaN(priorityFeatureSet?.features?.length)) {
        setPrioritiesTotalCount(priorityFeatureSet?.features?.length)
      }
      if (!isNaN(structuresFeatureSet?.features?.length)) {
        setStructuresTotalCount(structuresFeatureSet?.features?.length);
        const uniqueAssetTypes = Array.from(new Set(
            structuresFeatureSet?.features?.map(
              (feature: any) => feature.attributes?.asset_type)
          ),
        );
        const structValues = uniqueAssetTypes.map((assetType: any) => ({
          id: assetType,
          label: assetType,
          color: "",
        }));
        dispatch(setStructureOptions(structValues));
      }
      if (!isNaN(intersectionFeatureSet?.features?.length)) {
        setIntersectionTotalCount(intersectionFeatureSet?.features?.length)
      }
      if (!isNaN(capacityPriorityFeatureSet?.features?.length)) {
        setCapacityOvertakingPrioritiesTotalCount(capacityPriorityFeatureSet?.features?.length)
      }
    }
    if (changeView.ready) {
      fetchUpdate()
    }
  }

  useEffect(() => {
      const updateStructureCount = async () => {
      if (!view) return;
      const structures = view?.layerViews?.items?.find(
        (l: any) => l.layer.title === "Structures"
      );
      const structWhereClause =
        selectedStructure.length > 0
          ? `asset_type IN (${selectedStructure.map(v => `'${v}'`).join(',')})`
          : '1=1';
      const structuresFeatureSet = await structures?.queryFeatures({
        geometry: view.extent,
        where: structWhereClause,
      });
      if(structures){
        structures.filter = { where: structWhereClause}
      }
      if (!isNaN(structuresFeatureSet?.features?.length)) {
        setStructuresTotalCount(structuresFeatureSet?.features?.length)
      }
    };
    updateStructureCount();
  }, [selectedStructure])

  return (
    <Box>
      {/* MAIN CONTENT */}
      <Grid container>
        {/* SIDEBAR */}
        <Grid
          size={{ xs: 12, md: 2.5 }}
          sx={{
            overflowY: 'auto',
            height: 'calc(100vh - 64px - 24px - 24px - 24.33px )' // screenHeight - navBarHeight - paddingTop - paddingBottom
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              overflowY: 'auto',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
              {cards.map((card) => (
                <DataCard
                  key={card.title}
                  title={card.title.toLocaleUpperCase()}
                  value={card.value}
                  description={card.description}
                  faq={card.faq}
                  sx={{
                    borderLeft: `3px solid ${card.borderColor}`,
                  }}
                  icon={card.icon}
                  borderColor={card.borderColor}
                />
              ))}
            </Box>

            <InfoBoxAlert icon='information'>
              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                Metrics are calculated based on the current corridor and visible map extent.
              </Typography>
            </InfoBoxAlert>
          </Paper>
        </Grid>
        {/* MAIN AREA */}
        <Grid
          size={{ xs: 12, md: 9.5 - rightSidebarGridSize }}
          sx={{
            overflowY: 'auto',
            height: 'calc(100vh - 64px - 24px - 24px - 24.33px )' // screenHeight - navBarHeightDefault - paddingTop - paddingBottom - navBarHeightAddtl
          }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: '100%',
            }}
          >
            {/* Your MapComponent fills the Paper */}
            <MapComponent mapItem={config.overviewMapItemId} priorityFilter={selectedPriority} 
              onViewChange={handleViewChange} 
            >
              <arcgis-expand expanded={false} id="map-widget-firstposition" slot="bottom-left" expandTooltip="Legend">
                <arcgis-legend />
              </arcgis-expand>
              <arcgis-expand id="map-filter-expand" slot="top-left" expand-icon="filter" expand-tooltip="Structure Filter">
                <div style={{width: '10vw', padding: '1em'}}>
                  <FilterWidget
                    title="Filter by Structure"
                    selected={selectedStructure}
                    options={structureOptions}
                    onChange={setSelectedStructure}
                  />
                </div>
              </arcgis-expand>
              {/* <arcgis-expand expanded id="map-filter-expand" slot="top-left" expand-icon="filter" expand-tooltip="Priority Filter">
                <div style={{width: '10vw', padding: '1em'}}>
                  <FilterWidget
                    title="Filter by Priority"
                    selected={selectedPriority}
                    options={priorityOptions}
                    onChange={setSelectedPriority}
                  />
                </div>
              </arcgis-expand>
              <arcgis-expand id="map-filter-expand" slot="top-left" expand-icon="filter" expand-tooltip="Treatment Filter">
                <div style={{width: '10vw', padding: '1em'}}>
                  <FilterWidget
                    title="Filter by Treatment"
                    selected={selectedTreatment}
                    options={treatmentOptions}
                    onChange={setSelectedTreatment}
                  />
                </div>
              </arcgis-expand> */}
            </MapComponent>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}